import { Plus, Trash2, X } from "lucide-react";
import { ItemPicker } from "@/components/ItemPicker";
import { MarkupText } from "@/components/trial/MarkupText";
import { RewardIcon } from "@/components/players/PlayerInspector";
import type { TrialStage, TrialIssue, SpawnData, MonolithValues, StageModifier } from "@/lib/trial-schema";
import { MONOLITH_EFFECTS, CHALLENGE_TYPES, monolithDescription, challengeDescription, usesAttackPotency } from "@/lib/trial-defense";
import { TRIAL_ENEMIES, TRIAL_SPAWN_POSITIONS } from "@/lib/trial-catalog";
import { REWARD_TYPE_NAMES } from "@/types/cloud-data";
import {
  addWave, removeWave, addSpawn, removeSpawn, updateSpawn, addChallenge, updateChallenge,
  addReward, updateReward, updateDefenseSystem, updateMonolithValues, updateModifier,
} from "@/lib/trial-edit";

type Updater = (updater: (s: TrialStage) => TrialStage) => void;
const toNumber = (raw: string) => raw === "" ? 0 : Number(raw);

export function FloorDetail({ stage, issues, onUpdate }: { stage: TrialStage; issues: TrialIssue[]; onUpdate: Updater }) {
  // Only blocking errors are surfaced; warnings were noise.
  const errors = issues.filter(issue => issue.severity === "error");
  return <div className="tr-floor">
    <h2 className="tr-floor-title">Floor {stage.floor}</h2>
    {errors.length > 0 && <div className="ws-alert tr-errors" role="alert">
      <strong>Fix before saving</strong>
      <ul>{errors.map((issue, i) => <li key={i}>{issue.message}</li>)}</ul>
    </div>}
    <div className="tr-split">
      <Card title="Defense system"><DefenseSystemSection stage={stage} onUpdate={onUpdate} /></Card>
      <Card title="Enemy stats"><ModifiersSection stage={stage} onUpdate={onUpdate} /></Card>
    </div>
    <Card title="Waves" action={<button type="button" className="pw-button tr-small" onClick={() => onUpdate(addWave)}><Plus size={12} />Wave</button>}>
      <WavesSection stage={stage} onUpdate={onUpdate} />
    </Card>
    <Card title="Stars"><StarsSection stage={stage} onUpdate={onUpdate} /></Card>
  </div>;
}

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return <section className="ws-card ws-form tr-card">
    <div className="tr-card-head"><h3>{title}</h3>{action}</div>
    {children}
  </section>;
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return <label className="ws-field">{label}
    <input type="number" value={Number.isFinite(value) ? value : 0} onChange={e => onChange(toNumber(e.target.value))} /></label>;
}

/** A <select> that never silently jumps off an out-of-range stored value. */
function SelectWithFallback({ value, options, onChange, className, label }: { value: number; options: { value: number; label: string }[]; onChange: (v: number) => void; className?: string; label: string }) {
  const known = options.some(o => o.value === value);
  return <select aria-label={label} value={value} onChange={e => onChange(parseInt(e.target.value, 10))} className={className}>
    {!known && <option value={value}>Unknown ({value})</option>}
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>;
}

function DefenseSystemSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const showValues = stage.defenseSystem !== 0; // TrialStage's own [ShowIf]
  const patchValues = (patch: Partial<MonolithValues>) => onUpdate(s => updateMonolithValues(s, patch));
  const v = stage.monolithValues;
  return <>
    <SelectWithFallback label="Monolith attack" value={stage.defenseSystem} options={MONOLITH_EFFECTS.map(e => ({ value: e.value, label: e.name }))}
      onChange={value => onUpdate(s => updateDefenseSystem(s, value))} className="tr-monolith" />
    {showValues ? <>
      <p className="tr-description"><MarkupText text={monolithDescription(stage.defenseSystem, stage.monolithValues)} /></p>
      <div className="tr-grid is-three">
        <NumberField label="Activation time" value={v.activationTime} onChange={x => patchValues({ activationTime: x })} />
        <NumberField label="Effect duration" value={v.effectDuration} onChange={x => patchValues({ effectDuration: x })} />
        {usesAttackPotency(stage.defenseSystem) && <NumberField label="Attack potency" value={v.attackPotency} onChange={x => patchValues({ attackPotency: x })} />}
      </div>
      <label className="tr-check"><input type="checkbox" checked={v.hitInvincibleState} onChange={e => patchValues({ hitInvincibleState: e.target.checked })} />Can hit invincible targets</label>
    </> : <p className="ws-muted">No monolith on this floor.</p>}
  </>;
}

/** EXP and gold multipliers are always 0 in practice, so only the enemy stat multipliers are editable here. */
function ModifiersSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const m = stage.modifier;
  const patch = (p: Partial<StageModifier>) => onUpdate(s => updateModifier(s, p));
  return <div className="tr-mods">
    <NumberField label="HP Multiplier" value={m.hpMultiplier} onChange={v => patch({ hpMultiplier: v })} />
    <NumberField label="ATK Multiplier" value={m.damageMultiplier} onChange={v => patch({ damageMultiplier: v })} />
  </div>;
}

const ENEMIES_BY_CLASS = { Creep: TRIAL_ENEMIES.filter(e => e.class === "Creep"), Boss: TRIAL_ENEMIES.filter(e => e.class === "Boss") };

function SpawnRow({ spawn, label, onChange, onRemove }: { spawn: SpawnData; label: string; onChange: (patch: Partial<SpawnData>) => void; onRemove?: () => void }) {
  return <div className="tr-spawn" role="row">
    <select role="cell" aria-label={`${label} enemy`} className="tr-spawn-enemy" value={spawn._enemyIndex} onChange={e => onChange({ _enemyIndex: parseInt(e.target.value, 10) })}>
      {!TRIAL_ENEMIES.some(e => e.index === spawn._enemyIndex) && <option value={spawn._enemyIndex}>Unknown ({spawn._enemyIndex})</option>}
      {(["Creep", "Boss"] as const).map(group => <optgroup key={group} label={group}>
        {ENEMIES_BY_CLASS[group].map(en => <option key={en.index} value={en.index}>{en.name}</option>)}
      </optgroup>)}
    </select>
    <select role="cell" aria-label={`${label} position`} className="tr-spawn-pos" value={spawn._spawnPos} onChange={e => onChange({ _spawnPos: parseInt(e.target.value, 10) })}>
      <option value={-1}>Random</option>
      {!TRIAL_SPAWN_POSITIONS.some(p => p.index === spawn._spawnPos) && spawn._spawnPos !== -1 && <option value={spawn._spawnPos}>Unknown ({spawn._spawnPos})</option>}
      {TRIAL_SPAWN_POSITIONS.map(p => <option key={p.index} value={p.index}>{p.name ?? `Position ${p.index}`}</option>)}
    </select>
    <label role="cell" className="tr-spawn-force" title="Force level"><input type="checkbox" aria-label={`${label} force level`} checked={spawn._forceLevel} onChange={e => onChange({ _forceLevel: e.target.checked })} /><span>Force lvl</span></label>
    <input role="cell" type="number" aria-label={`${label} level`} title="Level" placeholder="Lv" className="tr-spawn-level" value={spawn._level} onChange={e => onChange({ _level: toNumber(e.target.value) })} />
    <span role="cell" className="tr-spawn-remove">{onRemove && <button type="button" className="pw-icon-button tr-remove" aria-label={`Remove ${label}`} onClick={onRemove}><X size={14} /></button>}</span>
  </div>;
}

function WavesSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  return <div className="tr-spawns" role="table" aria-label="Wave spawns">
    <div className="tr-spawn tr-spawn-head" role="row"><span role="columnheader">Enemy</span><span role="columnheader">Position</span><span role="columnheader">Force lvl</span><span role="columnheader">Level</span><span /></div>
    {stage.waveData.map((wave, wi) => <div key={wi} className="tr-wave" role="rowgroup">
      <div className="tr-wave-head" role="row"><span role="rowheader">Wave {wi + 1}</span>
        <button type="button" className="pw-icon-button tr-small" aria-label={`Add spawn to wave ${wi + 1}`} title="Add spawn" onClick={() => onUpdate(s => addSpawn(s, wi))}><Plus size={13} /></button>
        {stage.waveData.length > 1 && <button type="button" className="pw-icon-button tr-small tr-remove" aria-label={`Remove wave ${wi + 1}`} title="Remove wave" onClick={() => onUpdate(s => removeWave(s, wi))}><Trash2 size={13} /></button>}
      </div>
      {wave.spawnData.map((sp, si) => <SpawnRow key={si} spawn={sp} label={`Wave ${wi + 1} spawn ${si + 1}`} onChange={patch => onUpdate(s => updateSpawn(s, wi, si, patch))}
        onRemove={wave.spawnData.length > 1 ? () => onUpdate(s => removeSpawn(s, wi, si)) : undefined} />)}
    </div>)}
  </div>;
}

/** Fixed three stars: the challenge that earns each one and the reward for reaching that star count. */
function StarsSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const addStar = () => onUpdate(s => {
    const next = addChallenge(s);
    return next.reward.length < next.challenges.length ? addReward(next) : next;
  });
  return <div className="tr-stars">
    {[0, 1, 2].map(i => {
      const c = stage.challenges[i];
      const r = stage.reward[i];
      const showAmount = c && (c.challengeType === 2 || c.challengeType === 3); // LimitedConsumable / ClearWithin
      return <div key={i} className="tr-star-row">
        <span className="tr-star">{i + 1} ★</span>
        <div className="tr-star-challenge">
          {c ? <>
            <div className="tr-star-inputs">
              <SelectWithFallback label={`Star ${i + 1} challenge`} value={c.challengeType} options={CHALLENGE_TYPES.map(ct => ({ value: ct.value, label: ct.name }))}
                onChange={v => onUpdate(s => updateChallenge(s, i, { challengeType: v }))} />
              {showAmount && <input type="number" aria-label={`Star ${i + 1} challenge amount`} placeholder="Amount" className="tr-star-amount" value={c.amount}
                onChange={e => onUpdate(s => updateChallenge(s, i, { amount: toNumber(e.target.value) }))} />}
            </div>
            <small><MarkupText text={challengeDescription(c.challengeType, c.amount)} /></small>
          </> : i === stage.challenges.length
            ? <button type="button" className="pw-button tr-small tr-star-add" onClick={addStar}><Plus size={12} />Set challenge</button>
            : <small className="ws-muted">Set star {stage.challenges.length + 1} first</small>}
        </div>
        <div className="tr-star-reward">
          {r ? <>
            <span className="tr-reward-icon"><RewardIcon reward={{ rewardType: r.rewardType, amount: r.amount, itemID: r.itemID || undefined }} /></span>
            <SelectWithFallback label={`Star ${i + 1} reward type`} value={r.rewardType} options={Object.entries(REWARD_TYPE_NAMES).map(([v, label]) => ({ value: Number(v), label }))}
              onChange={v => onUpdate(s => updateReward(s, i, { rewardType: v }))} className="tr-star-reward-type" />
            {r.rewardType === 0 && <ItemPicker className="tr-item-picker tr-star-item" value={r.itemID} placeholder="Search items" onChange={id => onUpdate(s => updateReward(s, i, { itemID: id }))} />}
            <input type="number" aria-label={`Star ${i + 1} reward amount`} placeholder="Amount" className="tr-reward-amount" value={r.amount}
              onChange={e => onUpdate(s => updateReward(s, i, { amount: toNumber(e.target.value) }))} />
          </> : i === stage.reward.length
            ? <button type="button" className="pw-button tr-small" onClick={() => onUpdate(addReward)}><Plus size={12} />Set reward</button>
            : <small className="ws-muted">Set star {stage.reward.length + 1} first</small>}
        </div>
      </div>;
    })}
  </div>;
}
